import PlanningApplicationsService from "@publicPortal/services/PlanningApplicationsService";
import { Serializer } from "@shared/mirage/helpers/Serializer";
import { memo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PlanningApplicationSearchState } from "./PlanningApplicationSearchState";
import PlanningApplicationSimpleSearch from "./PlanningApplicationSimpleSearch";
import { EntityTableStateStorage } from "@mirage/components/entityTable/EntityTableStateStorage";
import PublicPortalState from "@publicPortal/utils/InitialState";
import { PlanningApplicationsFilter } from "@shared/publicportal/contracts/PlanningApplicationsFilter";
import { atom, useRecoilState } from "recoil";
import SearchTypeSwitchingTabs from "@publicPortal/components/advancedSearch/SearchTypeSwitchingTabs";
import SearchActions from "../SearchActions";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { SearchType } from "@shared/publicportal/contracts/Search";
import PlanningApplicationAdvancedSearch from "@publicPortal/components/advancedSearch/PlanningApplicationAdvancedSearch";
import { PublicPortalMapSearch } from "@publicPortal/components/mapSearch/PublicPortalMapSearch";

const planningAppService = new PlanningApplicationsService();
const { initialSearchState, initialFilterState } = PublicPortalState.planningApplicationInitialSearchState();
const planningApplicationsSearchState = atom({
    key: "planningApplicationsSearchState",
    default: initialSearchState,
});
const pp = new PublicPortalRoutingService();

const PlanningApplicationSearch = memo(() => {
    const navigate = useNavigate();
    const [state, setState] = useRecoilState<PlanningApplicationSearchState>(planningApplicationsSearchState);
    const onSearchTypeChanged = useCallback(
        (newSearchType: SearchType) => {
            setState((prev) => {
                return { ...prev, searchType: newSearchType };
            });
        },
        [setState]
    );
    const setStateModifier = useCallback(
        (modifier: Partial<PlanningApplicationsFilter>) => {
            setState((prev) => {
                return { ...prev, filter: { ...prev.filter, ...modifier } };
            });
        },
        [setState]
    );
    const onClear = useCallback(() => {
        setState((prev) => {
            return {
                ...prev,
                filter: {
                    ...initialFilterState,
                },
            };
        });
    }, [setState]);
    const prepareFilter = useCallback(() => {
        switch (state.searchType) {
            case "Simple": {
                return { searchType: state.searchType, appRef: state.filter.appRef, status: state.filter.status };
            }
            case "Advanced": {
                return { ...state.filter, searchType: state.searchType };
            }
            case "Map": {
                return { searchType: state.searchType, geometry: state.filter.geometry };
            }
            default:
                return null;
        }
    }, [state.filter, state.searchType]);
    const onSubmit = useCallback(() => {
        const filter = prepareFilter();
        const searchParams = Serializer.toQueryParamsNotEmptyOnly(filter);
        EntityTableStateStorage.clear("planningApplications");
        navigate(`${pp.SearchRoute("PlanningApplications", searchParams)}`);
    }, [navigate, prepareFilter]);

    useEffect(() => {
        const getFilteringLists = async () => {
            const filter = await planningAppService.getFilteringLists();
            setState((prev) => {
                return {
                    ...prev,
                    appTypes: filter?.ApplicationTypes || [],
                    decisions: filter?.DecisionTypes || [],
                    wards: filter?.Wards || [],
                    parishes: filter?.Parishes || [],
                    applicationStatuses: filter?.ApplicationStatuses || [],
                };
            });
        };
        getFilteringLists();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <SearchTypeSwitchingTabs searchType={state.searchType} onSearchTypeChanged={onSearchTypeChanged} />
            {getForm()}
            <SearchActions onCancelHandler={onClear} onSubmitHandler={onSubmit} />
        </>
    );

    function getForm() {
        switch (state.searchType) {
            case "Simple": {
                return <PlanningApplicationSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <PlanningApplicationAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return <PublicPortalMapSearch geometry={state.filter.geometry} setStateModifier={setStateModifier} />;
            }
            default:
                return null;
        }
    }
});

export default PlanningApplicationSearch;
